import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/types';
import { queryClient } from '@/lib/queryClient';

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: User | null;
  organizationId: string | null;
  isAuthenticated: boolean;

  // Impersonation snapshot of admin's own session
  originalToken: string | null;
  originalRefreshToken: string | null;
  originalUser: User | null;
  originalOrganizationId: string | null;

  setTokens: (token: string, refreshToken: string) => void;
  setUser: (user: User) => void;
  setOrganizationId: (id: string) => void;
  login: (token: string, refreshToken: string, user: User) => void;
  logout: () => void;
  impersonate: (token: string, user: User) => void;
  stopImpersonating: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      user: null,
      organizationId: null,
      isAuthenticated: false,

      originalToken: null,
      originalRefreshToken: null,
      originalUser: null,
      originalOrganizationId: null,

      setTokens: (token, refreshToken) =>
        set({ token, refreshToken, isAuthenticated: true }),

      setUser: (user) => set({ user }),

      setOrganizationId: (organizationId) => set({ organizationId }),

      login: (token, refreshToken, user) => {
        // Drop any cached data belonging to a previous session
        queryClient.clear();
        set({
          token,
          refreshToken,
          user,
          organizationId: null,
          isAuthenticated: true,
        });
      },

      logout: () => {
        queryClient.clear();
        set({
          token: null,
          refreshToken: null,
          user: null,
          organizationId: null,
          isAuthenticated: false,
          originalToken: null,
          originalRefreshToken: null,
          originalUser: null,
          originalOrganizationId: null,
        });
      },

      impersonate: (token, user) => {
        const state = get();
        if (!state.originalToken) {
          set({
            originalToken: state.token,
            originalRefreshToken: state.refreshToken,
            originalUser: state.user,
            originalOrganizationId: state.organizationId,
          });
        }
        queryClient.clear();
        set({
          token,
          refreshToken: null,
          user,
          organizationId: null,
          isAuthenticated: true,
        });
      },

      stopImpersonating: () => {
        const state = get();
        if (!state.originalToken) return;
        queryClient.clear();
        set({
          token: state.originalToken,
          refreshToken: state.originalRefreshToken,
          user: state.originalUser,
          organizationId: state.originalOrganizationId,
          isAuthenticated: true,
          originalToken: null,
          originalRefreshToken: null,
          originalUser: null,
          originalOrganizationId: null,
        });
      },
    }),
    {
      name: 'aw-auth',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        organizationId: state.organizationId,
        isAuthenticated: state.isAuthenticated,
        originalToken: state.originalToken,
        originalRefreshToken: state.originalRefreshToken,
        originalUser: state.originalUser,
        originalOrganizationId: state.originalOrganizationId,
      }),
    },
  ),
);

export const isSuperAdmin = (user: User | null): boolean =>
  !!user?.roles?.includes('ROLE_SUPER_ADMIN');
